import React, { useEffect, useState } from "react";
import {
	getAdminUsers,
	updateUserRole,
	updateUserStatus,
	createAdminUser,
} from "../../services/adminService";

const EMPTY_FORM = {
	first_name: "",
	last_name: "",
	email: "",
	phone: "",
	role: "client",
	password: "",
	confirm_password: "",
	is_active: true,
	marketing_consent: false,
};

const AdminUsersPage = () => {
	const [users, setUsers] = useState([]);
	const [loading, setLoading] = useState(true);

	const [updatingUserId, setUpdatingUserId] = useState(null);

	const [showAddModal, setShowAddModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);

	const [selectedUser, setSelectedUser] = useState(null);
	const [form, setForm] = useState(EMPTY_FORM);

	const [saving, setSaving] = useState(false);

	useEffect(() => {
		const loadUsers = async () => {
			try {
				const data = await getAdminUsers();
				setUsers(data);
			} catch (error) {
				console.error(
					"Erreur chargement utilisateurs:",
					error?.message || error,
				);
			} finally {
				setLoading(false);
			}
		};

		loadUsers();
	}, []);

	const handleFormChange = (event) => {
		const { name, value, type, checked } = event.target;

		setForm((prev) => ({
			...prev,
			[name]: type === "checkbox" ? checked : value,
		}));
	};

	const openAddModal = () => {
		setSelectedUser(null);
		setForm(EMPTY_FORM);
		setShowAddModal(true);
	};

	const openEditModal = (user) => {
		setSelectedUser(user);

		setForm({
			first_name: user.first_name || "",
			last_name: user.last_name || "",
			email: user.email || "",
			phone: user.phone || "",
			role: user.role || "client",
			password: "",
			confirm_password: "",
			is_active: user.is_active ?? true,
			marketing_consent: user.marketing_consent ?? false,
		});

		setShowEditModal(true);
	};

	const closeModals = () => {
		if (saving) return;

		setShowAddModal(false);
		setShowEditModal(false);
		setSelectedUser(null);
		setForm(EMPTY_FORM);
	};

	const handleRoleChange = async (userId, newRole) => {
		try {
			setUpdatingUserId(userId);

			const updatedUser = await updateUserRole(userId, newRole);

			setUsers((prev) =>
				prev.map((user) => (user.id === userId ? updatedUser : user)),
			);
		} catch (error) {
			console.error("Erreur mise à jour rôle:", error?.message || error);
		} finally {
			setUpdatingUserId(null);
		}
	};

	const handleStatusChange = async (userId, isActive) => {
		try {
			setUpdatingUserId(userId);

			const updatedUser = await updateUserStatus(userId, isActive);

			setUsers((prev) =>
				prev.map((user) => (user.id === userId ? updatedUser : user)),
			);
		} catch (error) {
			console.error("Erreur mise à jour statut:", error?.message || error);
		} finally {
			setUpdatingUserId(null);
		}
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		if (!form.first_name.trim()) {
			return;
		}

		if (!form.last_name.trim()) {
			return;
		}

		if (!form.email.trim()) {
			return;
		}

		try {
			setSaving(true);

			if (!selectedUser && form.role === "admin") {
				if (!form.password || form.password.length < 8) {
					console.error("Le mot de passe doit contenir au moins 8 caractères.");
					return;
				}

				if (form.password !== form.confirm_password) {
					console.error("Les mots de passe ne correspondent pas.");
					return;
				}
			}

			if (selectedUser) {
				// Mise à jour d'un utilisateur existant
				// À brancher avec updateUserProfile()
				return;
			}

			const result = await createAdminUser({
				email: form.email.trim(),
				first_name: form.first_name.trim(),
				last_name: form.last_name.trim(),
				phone: form.phone.trim(),
				role: form.role,
				password: form.role === "admin" ? form.password : undefined,
				is_active: form.is_active,
				marketing_consent: form.marketing_consent,
			});

			if (result?.profile) {
				setUsers((prev) => [result.profile, ...prev]);
			}

			closeModals();
		} catch (error) {
			console.error("Erreur création utilisateur:", error?.message || error);
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="max-w-7xl mx-auto px-4 pt-32 pb-10">
			{/* HEADER */}
			<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
				<div>
					<h1 className="text-3xl font-bold text-[#2A2A2A]">
						Gestion des utilisateurs
					</h1>

					<p className="text-[#6B6B6B] mt-2">
						Consulte, ajoute et gère les comptes utilisateurs, leurs accès et
						leurs rôles.
					</p>
				</div>

				<button
					type="button"
					onClick={openAddModal}
					className="px-5 py-3 rounded-xl bg-[#2A2A2A] text-white font-medium hover:opacity-90 transition"
				>
					+ Ajouter un utilisateur
				</button>
			</div>

			{/* TABLE */}
			<div className="bg-white border border-[#E6E1D6] rounded-2xl shadow-sm overflow-hidden">
				<div className="overflow-x-auto">
					<table className="w-full text-left">
						<thead className="bg-[#FFFCF8]">
							<tr>
								<th className="px-4 py-3 text-sm font-semibold text-[#2A2A2A]">
									Nom
								</th>

								<th className="px-4 py-3 text-sm font-semibold text-[#2A2A2A]">
									Email
								</th>

								<th className="px-4 py-3 text-sm font-semibold text-[#2A2A2A]">
									Téléphone
								</th>

								<th className="px-4 py-3 text-sm font-semibold text-[#2A2A2A]">
									Rôle
								</th>

								<th className="px-4 py-3 text-sm font-semibold text-[#2A2A2A]">
									Accès
								</th>

								<th className="px-4 py-3 text-sm font-semibold text-[#2A2A2A]">
									Newsletter
								</th>

								<th className="px-4 py-3 text-sm font-semibold text-[#2A2A2A]">
									Actions
								</th>
							</tr>
						</thead>

						<tbody>
							{!loading &&
								users.map((user) => {
									const isUpdating = updatingUserId === user.id;

									return (
										<tr key={user.id} className="border-t border-[#E6E1D6]">
											<td className="px-4 py-4 text-[#2A2A2A]">
												{[user.first_name, user.last_name]
													.filter(Boolean)
													.join(" ") || "—"}
											</td>

											<td className="px-4 py-4 text-[#2A2A2A]">
												{user.email || "—"}
											</td>

											<td className="px-4 py-4 text-[#2A2A2A]">
												{user.phone || "—"}
											</td>

											<td className="px-4 py-4">
												<span
													className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
														user.role === "admin"
															? "bg-[#2A2A2A] text-white"
															: "bg-[#F4F0E8] text-[#6B6B6B]"
													}`}
												>
													{user.role === "admin" ? "Administrateur" : "Client"}
												</span>
											</td>

											<td className="px-4 py-4">
												<span
													className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
														user.is_active
															? "bg-green-50 text-green-700"
															: "bg-red-50 text-red-700"
													}`}
												>
													{user.is_active ? "Actif" : "Bloqué"}
												</span>
											</td>

											<td className="px-4 py-4 text-[#2A2A2A]">
												{user.marketing_consent ? "Oui" : "Non"}
											</td>

											<td className="px-4 py-4">
												<div className="flex flex-wrap gap-2">
													<button
														type="button"
														onClick={() => openEditModal(user)}
														className="px-3 py-2 rounded-lg border border-[#E6E1D6] hover:bg-[#FFFCF8] transition"
													>
														Modifier
													</button>

													<button
														type="button"
														onClick={() =>
															handleStatusChange(user.id, !user.is_active)
														}
														disabled={isUpdating}
														className="px-3 py-2 rounded-lg border border-[#E6E1D6] hover:bg-[#FFFCF8] transition disabled:opacity-50"
													>
														{user.is_active ? "Bloquer" : "Activer"}
													</button>

													{user.role === "admin" ? (
														<button
															type="button"
															onClick={() =>
																handleRoleChange(user.id, "client")
															}
															disabled={isUpdating}
															className="px-3 py-2 rounded-lg border border-[#E6E1D6] hover:bg-[#FFFCF8] transition disabled:opacity-50"
														>
															Retirer admin
														</button>
													) : (
														<button
															type="button"
															onClick={() => handleRoleChange(user.id, "admin")}
															disabled={isUpdating}
															className="px-3 py-2 rounded-lg border border-[#E6E1D6] hover:bg-[#FFFCF8] transition disabled:opacity-50"
														>
															Passer admin
														</button>
													)}
												</div>
											</td>
										</tr>
									);
								})}
						</tbody>
					</table>
				</div>

				{loading && (
					<div className="p-6 text-[#6B6B6B]">
						Chargement des utilisateurs...
					</div>
				)}

				{!loading && users.length === 0 && (
					<div className="p-6 text-[#6B6B6B]">Aucun utilisateur trouvé.</div>
				)}
			</div>

			{/* MODALE AJOUT / MODIFICATION */}
			{(showAddModal || showEditModal) && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
					<div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden">
						<div className="flex items-center justify-between px-6 py-5 border-b border-[#E6E1D6]">
							<div>
								<h2 className="text-xl font-bold text-[#2A2A2A]">
									{selectedUser
										? "Modifier l'utilisateur"
										: "Ajouter un utilisateur"}
								</h2>

								<p className="text-sm text-[#6B6B6B] mt-1">
									Gère les informations et les accès du compte.
								</p>
							</div>

							<button
								type="button"
								onClick={closeModals}
								disabled={saving}
								className="text-2xl text-[#6B6B6B] hover:text-[#2A2A2A]"
							>
								×
							</button>
						</div>

						<form onSubmit={handleSubmit} className="p-6 space-y-6">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<label className="block text-sm font-medium text-[#2A2A2A] mb-2">
										Prénom
									</label>

									<input
										type="text"
										name="first_name"
										value={form.first_name}
										onChange={handleFormChange}
										className="w-full px-4 py-3 rounded-xl border border-[#E6E1D6] outline-none focus:border-[#2A2A2A]"
										placeholder="Jean"
										required
									/>
								</div>

								<div>
									<label className="block text-sm font-medium text-[#2A2A2A] mb-2">
										Nom
									</label>

									<input
										type="text"
										name="last_name"
										value={form.last_name}
										onChange={handleFormChange}
										className="w-full px-4 py-3 rounded-xl border border-[#E6E1D6] outline-none focus:border-[#2A2A2A]"
										placeholder="Dupont"
										required
									/>
								</div>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<label className="block text-sm font-medium text-[#2A2A2A] mb-2">
										Email
									</label>

									<input
										type="email"
										name="email"
										value={form.email}
										onChange={handleFormChange}
										disabled={!!selectedUser}
										className="w-full px-4 py-3 rounded-xl border border-[#E6E1D6] outline-none focus:border-[#2A2A2A] disabled:bg-[#F7F5F0]"
										placeholder="jean@email.com"
										required
									/>
								</div>

								<div>
									<label className="block text-sm font-medium text-[#2A2A2A] mb-2">
										Téléphone
									</label>

									<input
										type="tel"
										name="phone"
										value={form.phone}
										onChange={handleFormChange}
										className="w-full px-4 py-3 rounded-xl border border-[#E6E1D6] outline-none focus:border-[#2A2A2A]"
										placeholder="06 00 00 00 00"
									/>
								</div>
							</div>

							{!selectedUser && form.role === "admin" && (
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-medium text-[#2A2A2A] mb-2">
											Mot de passe temporaire
										</label>

										<input
											type="password"
											name="password"
											value={form.password}
											onChange={handleFormChange}
											className="w-full px-4 py-3 rounded-xl border border-[#E6E1D6] outline-none focus:border-[#2A2A2A]"
											placeholder="Minimum 8 caractères"
											autoComplete="new-password"
											required
											minLength={8}
										/>
									</div>

									<div>
										<label className="block text-sm font-medium text-[#2A2A2A] mb-2">
											Confirmer le mot de passe
										</label>

										<input
											type="password"
											name="confirm_password"
											value={form.confirm_password}
											onChange={handleFormChange}
											className="w-full px-4 py-3 rounded-xl border border-[#E6E1D6] outline-none focus:border-[#2A2A2A]"
											placeholder="Répéter le mot de passe"
											autoComplete="new-password"
											required
											minLength={8}
										/>
									</div>
								</div>
							)}

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<label className="block text-sm font-medium text-[#2A2A2A] mb-2">
										Rôle
									</label>

									<select
										name="role"
										value={form.role}
										onChange={handleFormChange}
										className="w-full px-4 py-3 rounded-xl border border-[#E6E1D6] bg-white outline-none focus:border-[#2A2A2A]"
									>
										<option value="client">Client</option>

										<option value="admin">Administrateur</option>
									</select>
								</div>

								<div>
									<label className="block text-sm font-medium text-[#2A2A2A] mb-2">
										Accès au compte
									</label>

									<label className="flex items-center gap-3 px-4 py-3 rounded-xl border border-[#E6E1D6] cursor-pointer">
										<input
											type="checkbox"
											name="is_active"
											checked={form.is_active}
											onChange={handleFormChange}
											className="h-4 w-4"
										/>

										<span className="text-sm text-[#2A2A2A]">
											Utilisateur autorisé à accéder à son compte
										</span>
									</label>
								</div>
							</div>

							<div>
								<label className="flex items-center gap-3 cursor-pointer">
									<input
										type="checkbox"
										name="marketing_consent"
										checked={form.marketing_consent}
										onChange={handleFormChange}
										className="h-4 w-4"
									/>

									<span className="text-sm text-[#2A2A2A]">
										Autoriser les communications marketing
									</span>
								</label>
							</div>

							{!selectedUser && (
								<div className="rounded-xl bg-[#FFFCF8] border border-[#E6E1D6] p-4">
									<p className="text-sm text-[#6B6B6B]">
										Le compte sera créé avec une invitation permettant à
										l'utilisateur de définir son mot de passe.
									</p>
								</div>
							)}

							<div className="flex justify-end gap-3 pt-2">
								<button
									type="button"
									onClick={closeModals}
									disabled={saving}
									className="px-5 py-3 rounded-xl border border-[#E6E1D6] hover:bg-[#FFFCF8] transition disabled:opacity-50"
								>
									Annuler
								</button>

								<button
									type="submit"
									disabled={saving}
									className="px-5 py-3 rounded-xl bg-[#2A2A2A] text-white hover:opacity-90 transition disabled:opacity-50"
								>
									{saving
										? "Enregistrement..."
										: selectedUser
											? "Enregistrer"
											: "Créer l'utilisateur"}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</div>
	);
};

export default AdminUsersPage;
